import React from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { isNativePlatform } from './utils/platform';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { AppLayout } from './components/layout/AppLayout';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { AuthAction } from './pages/AuthAction';
import { Dashboard } from './pages/Dashboard';
import { Settings } from './pages/Settings';
import { Users } from './pages/Users';
import { Upload } from './pages/Upload';
import { Footage } from './pages/Footage';
import { Clients } from './pages/Clients';
import { Appointments } from './pages/Appointments';
import SessionNotes from './pages/SessionNotes';
import ReviewNotes from './pages/ReviewNotes';
import BcbaReviewNotes from './pages/BcbaReviewNotes';
import LiveStreams from './pages/LiveStreams';
import { Profile } from './pages/Profile';
import { Timesheets } from './pages/Timesheets';
import { HRDashboard } from './pages/HRDashboard';
import LocationAuditTrail from './pages/LocationAuditTrail';
import { Reports } from './pages/Reports';
import { Calendar } from './pages/Calendar';
import MySessions from './pages/MySessions';
import ClinicalRecord from './pages/ClinicalRecord';
import DictateNotes from './pages/DictateNotes';
import WriteNotes from './pages/WriteNotes';
import InstallPrompt from './components/InstallPrompt';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfUse from './pages/legal/TermsOfUse';
import DataDeletion from './pages/legal/DataDeletion';
import Join from './pages/Join';
import { INVITE_ONBOARDING } from './utils/featureFlags';

// Web keeps clean history URLs; native (Capacitor) uses hash routing so
// deep links / reloads always resolve to index.html.
const Router = isNativePlatform() ? HashRouter : BrowserRouter;

// Protected Route wrapper with AppLayout
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4F6367]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Invite onboarding: a signed-in user not yet attached to a facility must
  // redeem an invite code before entering the app.
  if (INVITE_ONBOARDING && !user.companyId) {
    return <Navigate to="/join" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
};

// Auth required, but NO company gate and NO app shell — for /join, where a
// company-less user lands to redeem their invite.
const RequireAuthOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4F6367]"></div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/action" element={<AuthAction />} />
      {/* Public legal pages — accessible without login */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfUse />} />
      <Route path="/data-deletion" element={<DataDeletion />} />
      {/* Invite redemption — auth required, no company gate (flag-gated feature) */}
      <Route path="/join" element={<RequireAuthOnly><Join /></RequireAuthOnly>} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/upload"
        element={
          <ProtectedRoute>
            <Upload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/footage"
        element={
          <ProtectedRoute>
            <Footage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <Clients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:clientId/clinical"
        element={
          <ProtectedRoute>
            <ClinicalRecord />
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <Appointments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/session-notes"
        element={
          <ProtectedRoute>
            <SessionNotes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/review-notes"
        element={
          <ProtectedRoute>
            <BcbaReviewNotes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/review-notes-legacy"
        element={
          <ProtectedRoute>
            <ReviewNotes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/live-streams"
        element={
          <ProtectedRoute>
            <LiveStreams />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timesheets"
        element={
          <ProtectedRoute>
            <Timesheets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr-dashboard"
        element={
          <ProtectedRoute>
            <HRDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/location-audit"
        element={
          <ProtectedRoute>
            <LocationAuditTrail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <Calendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-sessions"
        element={
          <ProtectedRoute>
            <MySessions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-sessions/:sessionId/dictate"
        element={
          <ProtectedRoute>
            <DictateNotes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-sessions/:sessionId/notes"
        element={
          <ProtectedRoute>
            <WriteNotes />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      {/* Additional routes will be added here */}
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <CompanyProvider>
          <AppRoutes />
          <InstallPrompt />
        </CompanyProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
